/**
 * Key Card Component
 *
 * Displays a single API key with revoke action.
 *
 * @module components/keys/KeyCard
 */

import { Button, Tooltip } from '@/components/ui';
import { Key, Trash2 } from 'lucide-react';
import { cn, formatRelativeTime } from '@/lib/utils';
import type { ApiKey } from '@/lib/firebase-types';

// =============================================================================
// Types
// =============================================================================

export interface KeyCardProps {
  apiKey: ApiKey;
  onRevoke: (key: ApiKey) => void;
  /** Whether the user has permission to revoke keys (default: true) */
  canRevoke?: boolean;
}

// =============================================================================
// Component
// =============================================================================

export function KeyCard({ apiKey, onRevoke, canRevoke = true }: KeyCardProps) {
  const displayKey = `${apiKey.keyPrefix}...${apiKey.keySuffix}`;
  const isRevoked = apiKey.status === 'revoked';

  // Format dates
  const createdAt = apiKey.createdAt?.toDate
    ? formatRelativeTime(apiKey.createdAt.toDate())
    : 'Unknown';

  const lastUsedAt = apiKey.lastUsedAt?.toDate
    ? formatRelativeTime(apiKey.lastUsedAt.toDate())
    : null;

  return (
    <div
      className={cn(
        'flex items-center justify-between p-4 hover:bg-gray-50 transition-colors',
        isRevoked && 'opacity-60'
      )}
    >
      {/* Left side - Key info */}
      <div className="flex items-center gap-4">
        <div
          className={cn(
            'p-2 rounded-lg',
            isRevoked ? 'bg-gray-100' : 'bg-gray-100'
          )}
        >
          <Key
            className={cn(
              'h-5 w-5',
              isRevoked ? 'text-gray-400' : 'text-gray-600'
            )}
          />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <p
              className={cn(
                'font-medium',
                isRevoked ? 'text-gray-500 line-through' : 'text-gray-900'
              )}
            >
              {apiKey.label}
            </p>
          </div>
          <p className="text-sm text-gray-500 font-mono">{displayKey}</p>
        </div>
      </div>

      {/* Right side - Meta & Actions */}
      <div className="flex items-center gap-4">
        {/* Meta info */}
        <div className="text-right hidden sm:block">
          <p className="text-sm text-gray-900">
            {isRevoked ? 'Revoked' : lastUsedAt ? `Last used ${lastUsedAt}` : 'Never used'}
          </p>
          <p className="text-xs text-gray-500">Created {createdAt}</p>
        </div>

        {/* Revoke Action */}
        {!isRevoked && canRevoke && (
          <Tooltip content="Revoke this API key" position="top" width="auto">
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => onRevoke(apiKey)}
              aria-label="Revoke key"
              className="text-gray-500 hover:text-error-600"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </Tooltip>
        )}
      </div>
    </div>
  );
}
